import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ImagePlus, Info, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SeoEditor } from "../../components/SeoEditor";
import { StorefrontPreviewLink } from "../../components/StorefrontPreviewLink";
import {
  Button,
  Card,
  ErrorState,
  FormErrorSummary,
  ImageUploader,
  Input,
  Loading,
  Notice,
  PageHeader,
  RichTextEditor,
  Select,
  Switch,
  Textarea,
} from "../../components/ui";
import { adminApi } from "../../lib/api";
import { productSchema, type ProductFormValues } from "../../lib/form-schemas";
import { number, slugify } from "../../lib/format";
import type { ProductBadge, ProductGender, ProductStatus, SaveProductRequest } from "../../types/api";

const emptySeo = () => ({
  title: "",
  description: "",
  canonicalUrl: "",
  imageUrl: "",
  index: true,
  follow: true,
});

const emptyVariant = () => ({
  sku: "",
  volume: "",
  price: 0,
  originalPrice: null,
  stock: 0,
  isDefault: false,
  isTester: false,
  isDecant: false,
  isActive: true,
});

const makeInitial = (): ProductFormValues => ({
  slug: "",
  name: "",
  subtitle: "",
  shortDescription: "",
  description: "",
  brandId: "",
  displayBrandName: "",
  gender: "Unisex",
  concentration: "Eau de Parfum",
  family: "",
  nature: "",
  season: "",
  occasion: "",
  countryOfOrigin: "",
  releaseYear: null,
  badge: "New",
  status: "Draft",
  isFeatured: false,
  topNotes: "",
  middleNotes: "",
  baseNotes: "",
  categoryIds: [],
  variants: [{ ...emptyVariant(), isDefault: true }],
  images: [],
  seo: emptySeo(),
  searchMetadata: { gtin: "", mpn: "", googleProductCategory: "" },
});

function errorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const candidate = error as { message?: unknown; root?: { message?: unknown } };
  if (typeof candidate.message === "string") return candidate.message;
  if (typeof candidate.root?.message === "string") return candidate.root.message;
  return undefined;
}

function normalizeProduct(values: ProductFormValues): SaveProductRequest {
  return {
    ...values,
    slug: values.slug.trim(),
    name: values.name.trim(),
    subtitle: values.subtitle.trim(),
    displayBrandName: values.displayBrandName?.trim() || null,
    shortDescription: values.shortDescription?.trim() || null,
    description: values.description || null,
    family: values.family?.trim() || null,
    nature: values.nature?.trim() || null,
    season: values.season?.trim() || null,
    occasion: values.occasion?.trim() || null,
    countryOfOrigin: values.countryOfOrigin?.trim() || null,
    topNotes: values.topNotes?.trim() || null,
    middleNotes: values.middleNotes?.trim() || null,
    baseNotes: values.baseNotes?.trim() || null,
    variants: values.variants.map((variant) => ({
      ...variant,
      sku: variant.sku.trim().toUpperCase(),
      volume: variant.volume.trim(),
      originalPrice: variant.originalPrice ?? null,
    })),
    images: values.images.map((image, index) => ({
      ...image,
      alt: image.alt?.trim() || null,
      order: index,
    })),
    seo: {
      ...values.seo,
      title: values.seo.title?.trim() || null,
      description: values.seo.description?.trim() || null,
      canonicalUrl: values.seo.canonicalUrl?.trim() || null,
      imageUrl: values.seo.imageUrl?.trim() || null,
    },
    searchMetadata: {
      gtin: values.searchMetadata.gtin?.trim() || null,
      mpn: values.searchMetadata.mpn?.trim() || null,
      googleProductCategory: values.searchMetadata.googleProductCategory?.trim() || null,
    },
  };
}

export function ProductEditorPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialized = useRef(false);
  const [message, setMessage] = useState("");

  const detail = useQuery({
    queryKey: ["product", id],
    queryFn: () => adminApi.products.detail(id!),
    enabled: isEdit,
  });
  const brands = useQuery({ queryKey: ["brands"], queryFn: adminApi.brands.list });
  const categories = useQuery({ queryKey: ["categories"], queryFn: adminApi.categories.list });
  const meta = useQuery({
    queryKey: ["product-meta", detail.data?.slug],
    queryFn: () => adminApi.products.list({ search: detail.data!.slug, page: 1, pageSize: 100 }),
    enabled: Boolean(detail.data?.slug),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: makeInitial(),
  });

  const variantsFieldArray = useFieldArray({ control, name: "variants", keyName: "fieldKey" });
  const imagesFieldArray = useFieldArray({ control, name: "images", keyName: "fieldKey" });
  const values = useWatch({ control });
  const variants = values.variants ?? [];
  const images = values.images ?? [];
  const categoryIds = values.categoryIds ?? [];
  const searchMetadata = values.searchMetadata ?? { gtin: "", mpn: "", googleProductCategory: "" };

  useEffect(() => {
    initialized.current = false;
    if (!isEdit) reset(makeInitial());
  }, [id, isEdit, reset]);

  useEffect(() => {
    if (!detail.data || initialized.current) return;
    if (meta.isFetching && !meta.data) return;

    const item = detail.data;
    const adminMeta = meta.data?.items.find((entry) => entry.product.id === item.id);
    reset({
      slug: item.slug,
      name: item.name,
      subtitle: item.subtitle,
      shortDescription: item.shortDescription || "",
      description: item.description || "",
      brandId: item.brand.id,
      displayBrandName: item.brand.name,
      gender: item.gender as ProductGender,
      concentration: item.concentration,
      family: item.family || "",
      nature: item.nature || "",
      season: item.season || "",
      occasion: item.occasion || "",
      countryOfOrigin: item.countryOfOrigin || "",
      releaseYear: item.releaseYear ?? null,
      badge: item.badge as ProductBadge,
      status: adminMeta?.status ?? "Published",
      isFeatured: adminMeta?.isFeatured ?? false,
      topNotes: item.topNotes || "",
      middleNotes: item.middleNotes || "",
      baseNotes: item.baseNotes || "",
      categoryIds: item.categories.map((category) => category.id),
      variants: item.variants.map((variant) => ({ ...variant, originalPrice: variant.originalPrice ?? null, isActive: true })),
      images: item.images.map((image) => ({ ...image, alt: image.alt ?? "" })),
      seo: item.seo || emptySeo(),
      searchMetadata: item.searchMetadata || { gtin: "", mpn: "", googleProductCategory: "" },
    });
    initialized.current = true;
  }, [detail.data, meta.data, meta.isFetching, reset]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const mutation = useMutation({
    mutationFn: (body: SaveProductRequest) => isEdit ? adminApi.products.update(id!, body) : adminApi.products.create(body),
    onSuccess: async (saved) => {
      setMessage("اطلاعات محصول با موفقیت ذخیره شد.");
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      if (!isEdit) navigate(`/products/${saved.id}`, { replace: true });
      else reset(getValues());
    },
  });

  const addVariant = () => variantsFieldArray.append(emptyVariant());
  const addImage = () => {
    if (images.some((image) => !image?.url)) return;
    imagesFieldArray.append({
      url: "",
      alt: values.name || "",
      order: images.length,
      isPrimary: images.length === 0,
    });
  };

  const primaryImage = useMemo(() => images.findIndex((image) => image?.isPrimary), [images]);
  const hasUploadedImage = images.some((image) => Boolean(image?.url));
  const canAddAnotherImage = hasUploadedImage && images.every((image) => Boolean(image?.url));
  const summaryErrors = [
    errors.name?.message,
    errors.slug?.message,
    errors.subtitle?.message,
    errors.brandId?.message,
    errors.categoryIds?.message,
    errorMessage(errors.variants),
    errorMessage(errors.images),
    errors.seo?.title?.message,
    errors.seo?.canonicalUrl?.message,
    errors.searchMetadata?.gtin?.message,
  ];

  const submit = handleSubmit(
    (formValues) => {
      setMessage("");
      mutation.mutate(normalizeProduct(formValues));
    },
    () => {
      setMessage("چند مورد در فرم نیاز به اصلاح دارد. خطاهای مشخص‌شده را بررسی کنید.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  );

  if ((isEdit && (detail.isLoading || meta.isLoading)) || brands.isLoading || categories.isLoading) return <Loading />;
  const loadError = detail.error || brands.error || categories.error || meta.error;
  if (loadError) return <ErrorState error={loadError} />;

  return (
    <form className="product-editor-form" onSubmit={submit} noValidate>
      <PageHeader
        title={isEdit ? "ویرایش محصول" : "ثبت محصول جدید"}
        description="اطلاعات فروش، محتوای محصول، تنوع‌ها، تصاویر و داده‌های SEO را در یک جریان استاندارد و قابل کنترل مدیریت کنید."
        actions={
          <>
            <Link className="button button--ghost" to="/products"><ArrowRight size={17} /> بازگشت</Link>
            <StorefrontPreviewLink path={isEdit && values.slug ? `/products/${values.slug}` : undefined} disabled={!isEdit || !values.slug} />
            <Button type="submit" busy={mutation.isPending}><Save size={17} /> ذخیره محصول</Button>
          </>
        }
      />

      {message ? <Notice tone={summaryErrors.some(Boolean) ? "warning" : "success"}>{message}</Notice> : null}
      {mutation.error ? <Notice tone="warning">ذخیره انجام نشد: {mutation.error.message}</Notice> : null}
      <FormErrorSummary errors={summaryErrors} />

      <div className="editor-grid">
        <div className="editor-main">
          <Card title="اطلاعات اصلی" description="نام‌گذاری، برند، خلاصه و محتوای اصلی صفحه محصول">
            <div className="form-grid">
              <Input
                label="نام محصول"
                hint="نام کامل محصول همان‌طور که مشتری در فروشگاه می‌بیند."
                error={errors.name?.message}
                {...register("name", {
                  onChange: (event) => {
                    if (!isEdit && !getValues("slug")) {
                      setValue("slug", slugify(String(event.target.value)), { shouldDirty: true, shouldValidate: true });
                    }
                  },
                })}
              />
              <Input
                label="اسلاگ URL"
                dir="ltr"
                hint="فقط حروف انگلیسی کوچک، عدد و خط تیره؛ مثال: lia-azure-100ml"
                error={errors.slug?.message}
                {...register("slug", {
                  onChange: (event) => setValue("slug", slugify(String(event.target.value)), { shouldDirty: true, shouldValidate: true }),
                })}
              />
              <Input
                label="زیرعنوان"
                hint="یک عبارت کوتاه مثل «ادوپرفیوم یونیسکس با رایحه چوبی»."
                error={errors.subtitle?.message}
                {...register("subtitle")}
              />
              <Select
                label="برند"
                hint="برند باید پیش از محصول در بخش برندها ثبت شده باشد."
                error={errors.brandId?.message}
                {...register("brandId", {
                  onChange: (event) => {
                    const selected = brands.data?.find((brand) => brand.id === event.target.value);
                    setValue("displayBrandName", selected?.name || "", { shouldDirty: true });
                  },
                })}
              >
                <option value="">انتخاب برند</option>
                {brands.data?.filter((brand) => brand.isActive).map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
              </Select>
              <Textarea
                className="span-2"
                label="توضیح کوتاه"
                rows={3}
                maxLength={700}
                hint="خلاصه‌ای مفید و متقاعدکننده برای ابتدای صفحه محصول."
                error={errors.shortDescription?.message}
                {...register("shortDescription")}
              />
              <div className="span-2">
                <Controller
                  control={control}
                  name="description"
                  render={({ field, fieldState }) => (
                    <RichTextEditor
                      label="توضیحات کامل محصول"
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      hint="ساختار متن را با تیتر، پاراگراف و فهرست منظم کنید. محتوای صفحه باید برای خریدار مفید و منحصربه‌فرد باشد."
                      minHeight={400}
                    />
                  )}
                />
              </div>
            </div>
          </Card>

          <Card title="مشخصات رایحه" description="اطلاعاتی که به مقایسه و تصمیم‌گیری مشتری کمک می‌کنند">
            <div className="form-grid">
              <Select label="جنسیت" hint="برای محصولات مشترک گزینه یونیسکس را انتخاب کنید." {...register("gender")}>
                <option value="Women">زنانه</option>
                <option value="Men">مردانه</option>
                <option value="Unisex">یونیسکس</option>
              </Select>
              <Input label="غلظت" error={errors.concentration?.message} hint="مثال: Eau de Parfum یا Extrait de Parfum." {...register("concentration")} />
              <Input label="خانواده رایحه" hint="مثال: چوبی، گلی، شرقی، مرکباتی." {...register("family")} />
              <Input label="طبع" hint="مثال: گرم و تلخ، خنک و شیرین." {...register("nature")} />
              <Input label="فصل پیشنهادی" hint="مثال: پاییز و زمستان یا چهارفصل." {...register("season")} />
              <Input label="موقعیت استفاده" hint="مثال: رسمی، روزمره، مهمانی یا محل کار." {...register("occasion")} />
              <Input label="کشور سازنده" hint="کشور مبدا برند یا تولید محصول." {...register("countryOfOrigin")} />
              <Input
                label="سال عرضه"
                type="number"
                min="1900"
                max={new Date().getFullYear() + 1}
                error={errors.releaseYear?.message}
                hint="سال میلادی معرفی محصول؛ در صورت نامشخص بودن خالی بگذارید."
                {...register("releaseYear", { setValueAs: (value) => value === "" ? null : Number(value) })}
              />
              <Textarea label="نت آغازین" rows={4} hint="نت‌ها را با ویرگول جدا کنید." {...register("topNotes")} />
              <Textarea label="نت میانی" rows={4} hint="قلب رایحه را دقیق و کوتاه بنویسید." {...register("middleNotes")} />
              <Textarea className="span-2" label="نت پایه" rows={4} hint="نت‌های ماندگار و پایانی عطر." {...register("baseNotes")} />
            </div>
          </Card>

          <Card
            title="تنوع‌ها، قیمت و موجودی"
            description="هر SKU باید یکتا باشد و فقط یک تنوع فعال به‌عنوان پیش‌فرض انتخاب شود."
            action={<Button type="button" variant="secondary" onClick={addVariant}><Plus size={16} /> افزودن تنوع</Button>}
          >
            {errorMessage(errors.variants) ? <Notice tone="warning">{errorMessage(errors.variants)}</Notice> : null}
            <div className="variant-list mt-4">
              {variantsFieldArray.fields.map((field, index) => {
                const variant = variants[index] ?? field;
                return (
                  <div className="variant-card" key={field.fieldKey}>
                    <div className="variant-card__head">
                      <strong>تنوع {number(index + 1)} {variant.isDefault ? <span className="inline-pill">پیش‌فرض</span> : null}</strong>
                      <button type="button" title="حذف این تنوع" onClick={() => variantsFieldArray.remove(index)} disabled={variants.length === 1}><Trash2 /></button>
                    </div>
                    <div className="form-grid form-grid--compact">
                      <Input
                        label="SKU"
                        dir="ltr"
                        error={errors.variants?.[index]?.sku?.message}
                        hint="شناسه یکتای انگلیسی انبار؛ مثال LIA-AZURE-100."
                        {...register(`variants.${index}.sku`, {
                          onChange: (event) => setValue(`variants.${index}.sku`, String(event.target.value).toUpperCase().replace(/\s+/g, "-"), { shouldDirty: true, shouldValidate: true }),
                        })}
                      />
                      <Input label="حجم / عنوان تنوع" error={errors.variants?.[index]?.volume?.message} hint="مثال: ۱۰۰ میل، ۵۰ میل یا تستر ۵ میل." {...register(`variants.${index}.volume`)} />
                      <Input label="قیمت فروش (تومان)" type="number" min="1" error={errors.variants?.[index]?.price?.message} hint="قیمت نهایی پیش از اعمال کد تخفیف." {...register(`variants.${index}.price`, { valueAsNumber: true })} />
                      <Input label="قیمت قبل از تخفیف" type="number" min="0" error={errors.variants?.[index]?.originalPrice?.message} hint="اختیاری؛ فقط اگر از قیمت فروش بیشتر است." {...register(`variants.${index}.originalPrice`, { setValueAs: (value) => value === "" ? null : Number(value) })} />
                      <Input label="موجودی" type="number" min="0" error={errors.variants?.[index]?.stock?.message} hint="تعداد قابل فروش این تنوع در انبار." {...register(`variants.${index}.stock`, { valueAsNumber: true })} />
                      <div className="switch-stack">
                        <Controller
                          control={control}
                          name={`variants.${index}.isDefault`}
                          render={({ field: defaultField }) => (
                            <Switch
                              label="تنوع پیش‌فرض"
                              checked={Boolean(defaultField.value)}
                              hint="قیمت اولیه صفحه محصول از این تنوع خوانده می‌شود."
                              onChange={(checked) => {
                                if (checked) {
                                  setValue("variants", variants.map((item, itemIndex) => ({ ...item, isDefault: itemIndex === index })) as any, { shouldDirty: true, shouldValidate: true });
                                } else {
                                  defaultField.onChange(false);
                                }
                              }}
                            />
                          )}
                        />
                        <Controller control={control} name={`variants.${index}.isTester`} render={({ field: itemField }) => <Switch label="تستر" checked={Boolean(itemField.value)} hint="این تنوع به‌عنوان نسخه تستر معرفی می‌شود." onChange={itemField.onChange} />} />
                        <Controller control={control} name={`variants.${index}.isDecant`} render={({ field: itemField }) => <Switch label="دکانت" checked={Boolean(itemField.value)} hint="برای حجم‌های دست‌ریز یا دکانت فعال کنید." onChange={itemField.onChange} />} />
                        <Controller control={control} name={`variants.${index}.isActive`} render={({ field: itemField }) => <Switch label="فعال" checked={Boolean(itemField.value)} hint="تنوع غیرفعال برای خرید مشتری در دسترس نیست." onChange={itemField.onChange} />} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="section-bottom-action"><Button type="button" variant="secondary" onClick={addVariant}><Plus size={16} /> افزودن تنوع دیگر</Button></div>
          </Card>

          <Card
            title="گالری تصاویر"
            description="تصاویر باکیفیت، Alt دقیق و یک تصویر اصلی برای نمایش استاندارد محصول"
            action={canAddAnotherImage ? <Button type="button" variant="secondary" onClick={addImage}><ImagePlus size={16} /> افزودن تصویر دیگر</Button> : undefined}
          >
            {errorMessage(errors.images) ? <Notice tone="warning">{errorMessage(errors.images)}</Notice> : null}

            {!imagesFieldArray.fields.length ? (
              <div className="gallery-first-upload">
                <span><ImagePlus /></span>
                <div>
                  <strong>گالری محصول هنوز خالی است</strong>
                  <p>ابتدا اولین تصویر را اضافه کنید؛ پس از آپلود موفق، امکان افزودن تصاویر بیشتر نمایش داده می‌شود.</p>
                </div>
                <Button type="button" variant="secondary" onClick={addImage}><ImagePlus size={17} /> افزودن اولین تصویر</Button>
              </div>
            ) : (
              <div className={`image-list mt-4 ${imagesFieldArray.fields.length > 1 ? "image-list--grid" : ""}`}>
                {imagesFieldArray.fields.map((field, index) => {
                  const image = images[index] ?? field;
                  return (
                    <div className="image-card" key={field.fieldKey}>
                      <ImageUploader
                        label={`تصویر ${number(index + 1)}`}
                        value={image.url}
                        hint="WebP/JPEG/PNG با کیفیت مناسب و ابعاد هماهنگ با فروشگاه."
                        onChange={(url) => setValue(`images.${index}.url`, url, { shouldDirty: true, shouldValidate: true })}
                      />

                      <div className="image-card__alt-row">
                        <Input
                          label="متن جایگزین (Alt)"
                          error={errors.images?.[index]?.alt?.message}
                          hint="شرح واقعی و کوتاه تصویر برای دسترس‌پذیری و موتور جست‌وجو."
                          {...register(`images.${index}.alt`)}
                        />
                      </div>

                      <div className="image-card__action-row">
                        <Controller
                          control={control}
                          name={`images.${index}.isPrimary`}
                          render={({ field: primaryField }) => (
                            <Switch
                              label="تصویر اصلی"
                              checked={Boolean(primaryField.value)}
                              hint="این تصویر روی کارت محصول و ابتدای صفحه نمایش داده می‌شود."
                              onChange={(checked) => {
                                if (checked) {
                                  setValue("images", images.map((item, itemIndex) => ({ ...item, isPrimary: itemIndex === index })) as any, { shouldDirty: true, shouldValidate: true });
                                } else {
                                  primaryField.onChange(false);
                                }
                              }}
                            />
                          )}
                        />
                        <Button type="button" variant="danger" className="image-card__remove" onClick={() => imagesFieldArray.remove(index)}><Trash2 size={16} /> حذف تصویر</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {images.length > 0 && primaryImage < 0 ? <Notice tone="warning">برای نمایش صحیح در فروشگاه، یک تصویر را به‌عنوان تصویر اصلی انتخاب کنید.</Notice> : null}
            {canAddAnotherImage ? <div className="section-bottom-action"><Button type="button" variant="secondary" onClick={addImage}><ImagePlus size={16} /> افزودن تصویر دیگر</Button></div> : null}
          </Card>

          <Card title="SEO و داده‌های محصول" description="تنظیمات متادیتا، Canonical و شناسه‌های تجاری محصول">
            <Controller
              control={control}
              name="seo"
              render={({ field }) => (
                <SeoEditor
                  value={field.value}
                  onChange={field.onChange}
                  errors={{
                    title: errors.seo?.title?.message,
                    description: errors.seo?.description?.message,
                    canonicalUrl: errors.seo?.canonicalUrl?.message,
                    imageUrl: errors.seo?.imageUrl?.message,
                  }}
                  intro="متادیتا را برای همین صفحه بنویسید و از مقدارهای ساختگی یا متن تکراری استفاده نکنید."
                  titleHint="عنوان توصیفی، دقیق و منحصربه‌فرد برای نتیجه جست‌وجو بنویسید."
                  imageHint="URL تصویر شاخص متادیتا؛ در صورت خالی بودن از تصویر اصلی محصول استفاده می‌شود."
                  canonicalPlaceholder="https://example.com/products/..."
                />
              )}
            />
            <div className="seo-commerce-fields">
              <div className="section-label"><Info size={17} /><span>شناسه‌های تجاری و Merchant</span></div>
              <div className="form-grid form-grid--3">
                <Input label="GTIN" dir="ltr" inputMode="numeric" error={errors.searchMetadata?.gtin?.message} hint="شناسه جهانی معتبر محصول در صورت وجود." {...register("searchMetadata.gtin", { onChange: (event) => setValue("searchMetadata.gtin", String(event.target.value).replace(/\D/g, ""), { shouldDirty: true, shouldValidate: true }) })} />
                <Input label="MPN" dir="ltr" error={errors.searchMetadata?.mpn?.message} hint="شماره قطعه سازنده؛ اگر وجود ندارد خالی بگذارید." {...register("searchMetadata.mpn")} />
                <Input label="Google Product Category" value={searchMetadata.googleProductCategory || ""} error={errors.searchMetadata?.googleProductCategory?.message} hint="نام یا شناسه دسته مرتبط در طبقه‌بندی Google Merchant." {...register("searchMetadata.googleProductCategory")} />
              </div>
            </div>
          </Card>
        </div>

        <aside className="editor-side">
          <Card title="انتشار" description={isDirty ? "تغییرات ذخیره‌نشده دارید." : "فرم با آخرین نسخه ذخیره‌شده هماهنگ است."}>
            <div className="stack">
              <Select label="وضعیت" hint="پیش‌نویس در فروشگاه عمومی نمایش داده نمی‌شود." {...register("status")}>
                <option value="Draft">پیش‌نویس</option>
                <option value="Published">منتشرشده</option>
                <option value="Archived">بایگانی</option>
              </Select>
              <Select label="نشان محصول" hint="برچسب بازاریابی کوتاه روی کارت محصول." {...register("badge")}>
                <option value="New">جدید</option>
                <option value="Popular">محبوب</option>
                <option value="Niche">نیش</option>
                <option value="BestSeller">پرفروش</option>
                <option value="Special">ویژه</option>
              </Select>
              <Controller control={control} name="isFeatured" render={({ field }) => <Switch label="نمایش در بخش ویژه" checked={field.value} hint="در صورت فعال بودن، محصول می‌تواند در بخش‌های ویژه فروشگاه نمایش داده شود." onChange={field.onChange} />} />
            </div>
          </Card>

          <Card title="دسته‌بندی‌ها" description="حداقل یک دسته برای محصول انتخاب کنید.">
            {errors.categoryIds?.message ? <small className="field__error mb-3">{errors.categoryIds.message}</small> : null}
            <div className="check-list">
              {categories.data?.map((category) => (
                <label key={category.id}>
                  <input
                    type="checkbox"
                    checked={categoryIds.includes(category.id)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...categoryIds, category.id]
                        : categoryIds.filter((value) => value !== category.id);
                      setValue("categoryIds", next, { shouldDirty: true, shouldValidate: true });
                    }}
                  />
                  <span>{category.name}</span>
                </label>
              ))}
            </div>
          </Card>

          <Card title="کنترل نهایی">
            <div className="help-panel">
              <Info />
              <p>پیش از انتشار، تصویر اصلی، قیمت، موجودی، تنوع پیش‌فرض، دسته‌بندی و متن محصول را بازبینی کنید.</p>
            </div>
          </Card>

          <Button className="sticky-save" type="submit" busy={mutation.isPending}><Save size={17} /> ذخیره تغییرات</Button>
        </aside>
      </div>
    </form>
  );
}
